import { Box, Radio, GridLegacy as Grid, Tooltip, Typography, useTheme } from '@mui/material';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDialog, useMovieFinder, useSnack } from '@/logic/state';

import { ExperienceModel } from '@/logic/services/experiences';
import { FC } from 'react';

import { pxToRem } from '@/theme/typography';
import shallow from 'zustand/shallow';
import DoNotDisturbOnIcon from '@mui/icons-material/DoNotDisturbOn';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import ImageBoxFuture from '@/components/shared/image-box-future';

const ExperienceFallbackImg = '/icons/cinema/emptyExperienceImgCard.svg';
const NotAllowedIcon = '/icons/shared/not-allowed-icon.svg';
interface ExperienceCardProps {
  experience: ExperienceModel;
  withoutTitle?: boolean;
}

export const ExperienceCard: FC<ExperienceCardProps> = ({
  experience,
  withoutTitle = false,
}) => {
  const theme = useTheme();
  const [selectedExperiences, setSelectedExperiences] = useMovieFinder(
    s => [s.selectedExperiences, s.setSelectedExperiences],
    shallow
  );
  const intl = useIntl();
  const setSnack = useSnack(s => s.setSnack, shallow);
  const setDialog = useDialog(s => s.setDialog, shallow);

  const handleSelectedExperience = ({ id, isDisabled }: ExperienceModel): void => {
    if (isDisabled) {
      setSnack({
        isOpen: true,
        message: intl.formatMessage({
          id: 'movie_finder.not_available_option',
        }),
        icon: <ImageBoxFuture img={NotAllowedIcon} width='16px' />,
        type: 'info',
      });
      return;
    } else setSelectedExperiences(selectedExperiences.includes(id) ? [] : [id]);
  };

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        backgroundColor: theme.palette.common.white,
        border: `1px solid ${theme.palette.lightGrey.main}`,
        boxShadow: theme.customShadows.cardShadow,
        borderRadius: '20px',
        height: '106px',
        p: { tablet: pxToRem(20), mobile: '20px' },
        position: 'relative',
      }}
    >
      <Box sx={{ width: '30%' }}>
        <ImageBoxFuture
          img={experience?.logo}
          title={experience.logoAlt || experience?.name}
          fallBackImg={ExperienceFallbackImg}
          layout='intrinsic'
          objectFit='contain'
          width='794px'
          height='397px'
          sx={{
            width: {
              tablet: pxToRem(92),
              mobile: '92px',
            },
            display: 'flex',
            alignItems: 'center',
            marginRight: { tablet: pxToRem(20), mobile: '20px' },
            maxWidth: '100%',
          }}
        />
      </Box>
      <Box
        sx={{
          display: 'flex',
        }}
      >
        <Box>
          {!withoutTitle && (
            <Typography
              sx={{
                fontSize: {
                  mobile: '18px',
                  tablet: pxToRem(18),
                },
                color: theme.palette.grey[80],
                fontWeight: 700,
                fontFamily: theme.palette.typography.font.bold,
              }}
            >
              {experience.name}
            </Typography>
          )}
          <Tooltip title={experience.description}>
            <Grid
              container
              sx={{
                alignItems: 'flex-end',
              }}
            >
              <Grid item mobile={8}>
                <Typography
                  sx={{
                    fontSize: {
                      mobile: '14px',
                      tablet: pxToRem(16),
                    },
                    color: theme.palette.grey[50],
                    fontWeight: 400,
                    fontFamily: theme.palette.typography.font.regular,
                    WebkitLineClamp: { tablet: '2', mobile: '3' },
                    lineHeight: '24px',
                    display: '-webkit-box',
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {experience.description}
                </Typography>
              </Grid>
              <Grid item mobile={4}>
                <Typography
                  component='a'
                  sx={{
                    color: theme.palette.primary.main,
                    fontWeight: 700,
                    fontSize: pxToRem(16),
                    lineHeight: '24px',
                    cursor: 'pointer',
                  }}
                  onClick={() =>
                    setDialog({
                      wrapper: true,
                      isExperienceInfoDialog: true,
                      experienceInfoDialogPayload: experience,
                    })
                  }
                >
                  <FormattedMessage id='movie_details.read.more' />
                </Typography>
              </Grid>
            </Grid>
          </Tooltip>
        </Box>

        <Box
          sx={{
            // margin: pxToRem(18),
            position: 'absolute',
            top: '18px',
            right: '18px',
            '& .MuiButtonBase-root': {
              padding: 0,
            },
          }}
        >
          <Radio
            value={experience.id}
            checked={
              selectedExperiences.find(cinemaId => cinemaId === experience.id)
                ? true
                : false
            }
            icon={
              experience.isDisabled ? (
                <DoNotDisturbOnIcon />
              ) : (
                <RadioButtonUncheckedIcon />
              )
            }
            disabled={experience.isDisabled}
            sx={{
              'input:disabled ~ &': {
                color: theme.palette.grey[30],
              },
              '&.MuiCheckbox-indeterminate': {
                color: theme.palette.grey[30],
              },
              '&:hover': {
                backgroundColor: 'transparent',
              },
            }}
            onClick={() => handleSelectedExperience(experience)}
          />
        </Box>
      </Box>
    </Box>
  );
};
